/**
 * Custom hook for managing form submission
 */

import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../constants/apiConfig';
import { processStepData } from './formDataProcessors';

export const useFormSubmission = (stepOrder) => {
	const [isSaving, setIsSaving] = useState(false);
	const [fullPageLoader, setFullPageLoader] = useState(false);

	/**
	 * Build FormData from step data
	 */
	const buildFormData = (stepData, applicationId, originalLoadedData, currentStep = null, isSavePressed = false) => {
		const formData = new FormData();
		
		if (applicationId) {
			formData.append('applicationId', applicationId);
		}
		
		if (isSavePressed) {
			formData.append('sendEmailOnSave', 'true');
		}
		
		// Determine which steps to process
		const stepsToProcess = currentStep !== null 
			? stepOrder.slice(0, currentStep + 1)
			: stepOrder;
		
		// Process education step first (special handling)
		const education = stepData.educationalBackground;
		if (education && Object.keys(education).length > 0) {
			const success = processStepData('educationalBackground', formData, education, originalLoadedData.educationalBackground);
			if (!success) return null;
		}
		
		// Process other steps
		for (const stepKey of stepsToProcess) {
			if (stepKey === 'educationalBackground' || stepKey === 'preview') {
				continue;
			}
			
			const currentStepData = stepData[stepKey];
			if (!currentStepData || Object.keys(currentStepData).length === 0) {
				continue;
			}
			
			const success = processStepData(stepKey, formData, currentStepData, originalLoadedData[stepKey]);
			if (success === false) {
				return null;
			}
		}
		
		return formData;
	};

	/**
	 * Save form data
	 */
	const saveForm = async (stepData, applicationId, setApplicationId, originalLoadedData, currentStep = null, isSavePressed = false) => {
		setIsSaving(true);
		setFullPageLoader(true);
		
		try {
			const token = sessionStorage.getItem('token');
			const config = {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
			};
			
			const formDataToSend = buildFormData(stepData, applicationId, originalLoadedData, currentStep, isSavePressed);
			if (!formDataToSend) {
				setIsSaving(false);
				setFullPageLoader(false);
				return false;
			}
			
			const response = await axios.post(`${BASE_URL}/api/application/save`, formDataToSend, config);
			
			if (response?.data?.success) {
				// Set applicationId if it was returned from the save
				if (response.data.applicationId && !applicationId) {
					setApplicationId(response.data.applicationId);
					sessionStorage.setItem('applicationId', response.data.applicationId);
				}
				
				toast.success('Application saved successfully!');
				return true;
			} else {
				throw new Error('Failed to save application');
			}
		} catch (err) {
			console.error('Save error:', err);
			toast.error(`Failed to save application: ${err.response?.data?.message || err.message}`);
			return false;
		} finally {
			setIsSaving(false);
			setFullPageLoader(false);
		}
	};

	/**
	 * Submit final application
	 */
	const submitForm = async (stepData, applicationId, originalLoadedData, setIsSubmitted) => {
		setIsSaving(true);
		setFullPageLoader(true);
		
		try {
			const token = sessionStorage.getItem('token');
			const config = {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
			};
			
			// Build form data for all steps
			const formDataToSend = buildFormData(stepData, applicationId, originalLoadedData);
			if (!formDataToSend) {
				setIsSaving(false);
				setFullPageLoader(false);
				return false;
			}
			
			// Save first
			const response = await axios.post(`${BASE_URL}/api/application/save`, formDataToSend, config);
			
			if (response?.data?.success) {
				// Then submit
				try {
					const submitRes = await axios.post(
						`${BASE_URL}/api/application/${applicationId}/submit`,
						{},
						{ headers: { Authorization: `Bearer ${token}` } }
					);
					
					if (submitRes?.data?.success) {
						setIsSubmitted(true);
						toast.success('Application submitted successfully!');
						return true;
					} else {
						throw new Error(submitRes?.data?.message || 'Failed to submit application');
					}
				} catch (submitErr) {
					toast.error(
						`Application saved, but failed to submit: ${submitErr.response?.data?.message || submitErr.message}`
					);
					return false;
				}
			} else {
				throw new Error('Failed to submit application');
			}
		} catch (err) {
			toast.error(`Failed to submit application: ${err.response?.data?.message || err.message}`);
			return false;
		} finally {
			setIsSaving(false);
			setFullPageLoader(false);
		}
	};

	return {
		isSaving,
		fullPageLoader,
		saveForm,
		submitForm,
	};
};
